import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import type { glAccount } from "types/api-types/financial/gl-accounts/gl-accounts.types";
import type { department } from "types/api-types/system/departments/departments.types";
import type { fiscalPeriod } from "types/api-types/financial/fiscal-periods/fiscal-periods.types";
import type { evereePayable } from "types/api-types/payroll/everee-payables/everee-payables.types";
import type { evereeWorkerData } from "types/api-types/payroll/everee-worker-data/everee-worker-data.types";

/** Earning types we map to GL accounts. */
export const EARNING_TYPES = ["REG", "OT", "PTO", "VAC", "SICK", "BONUS"] as const;
export type EarningType = (typeof EARNING_TYPES)[number];

/** Maps Everee earningType values to our simplified categories. */
const EVEREE_EARNING_MAP: Record<string, EarningType> = {
  REGULAR_HOURLY: "REG",
  REGULAR_SALARY: "REG",
  OVERTIME_HOURLY: "OT",
  DOUBLE_TIME_HOURLY: "OT",
  VACATION: "VAC",
  SICK: "SICK",
  HOLIDAY: "PTO",
  BONUS: "BONUS",
  COMMISSION: "BONUS",
  REIMBURSEMENT: "REG",
  CONTRACTOR: "REG",
  TIPS: "REG",
  PER_DIEM: "REG",
  ADVANCE: "REG",
  SEPARATION: "REG",
  MILEAGE: "REG",
  EARNINGS_ON_DEMAND: "REG",
};

export interface GLMappingRule {
  glMappingId?: string;
  departmentId: string | null;
  earningType: EarningType;
  glAccountId: string;
  cashGlAccountId: string | null;
}

export interface GLOverride {
  overrideId?: string;
  workerId: string;
  workerName: string;
  earningType: EarningType;
  glAccountId: string;
  cashGlAccountId: string | null;
}

export interface JournalLine {
  accountId: string;
  accountNumber: string;
  accountName: string;
  description: string;
  debitAmount: number;
  creditAmount: number;
}

class PayrollGLMappingStore {
  rootStore: RootStore;

  // Data
  glAccounts: glAccount[] = [];
  departments: department[] = [];
  fiscalPeriods: fiscalPeriod[] = [];
  payables: evereePayable[] = [];
  workers: evereeWorkerData[] = [];

  // GL Mapping configuration
  mappingRules: GLMappingRule[] = [];
  overrides: GLOverride[] = [];

  // UI state
  currentTab = 0; // 0=Config, 1=Preview, 2=Export
  selectedPeriodId = "";
  isLoading = false;
  isGenerating = false;
  isPosting = false;
  error: string | null = null;
  successMessage: string | null = null;

  // Journal entry preview
  journalLines: JournalLine[] = [];
  journalDescription = "";

  // Dialog state
  overrideDialogOpen = false;
  editingOverride: GLOverride | null = null;
  exportDialogOpen = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Service store accessors
  get glAccountsStore(): ServiceStore {
    return this.rootStore.glAccountsStore as ServiceStore;
  }
  get departmentsStore(): ServiceStore {
    return this.rootStore.departmentsStore as ServiceStore;
  }
  get fiscalPeriodsStore(): ServiceStore {
    return this.rootStore.fiscalPeriodsStore as ServiceStore;
  }
  get evereePayablesStore(): ServiceStore {
    return this.rootStore.evereePayablesStore as ServiceStore;
  }
  get evereeWorkerDataStore(): ServiceStore {
    return this.rootStore.evereeWorkerDataStore as ServiceStore;
  }
  get journalEntriesStore(): ServiceStore {
    return this.rootStore.journalEntriesStore as ServiceStore;
  }
  get journalLineItemsStore(): ServiceStore {
    return this.rootStore.journalLineItemsStore as ServiceStore;
  }
  get payrollGlMappingsStore(): ServiceStore {
    return this.rootStore.payrollGlMappingsStore as ServiceStore;
  }
  get payrollGlOverridesStore(): ServiceStore {
    return this.rootStore.payrollGlOverridesStore as ServiceStore;
  }

  // Computed
  get companyId(): string {
    return (this.rootStore.routerStore?.queryParams?.companyId as string) || "NTX";
  }

  get selectedPeriod(): fiscalPeriod | undefined {
    return this.fiscalPeriods.find((p) => p.id === this.selectedPeriodId);
  }

  get openPeriods(): fiscalPeriod[] {
    return this.fiscalPeriods.filter((p) => p.status === "open");
  }

  get totalDebits(): number {
    return this.journalLines.reduce((sum, l) => sum + l.debitAmount, 0);
  }

  get totalCredits(): number {
    return this.journalLines.reduce((sum, l) => sum + l.creditAmount, 0);
  }

  get isBalanced(): boolean {
    return Math.abs(this.totalDebits - this.totalCredits) < 0.01;
  }

  get activeGlAccounts(): glAccount[] {
    return this.glAccounts.filter((a) => a.isActive);
  }

  getAccountDisplay = (accountId: string): string => {
    const acct = this.glAccounts.find((a) => a.id === accountId);
    return acct ? `${acct.accountNumber} - ${acct.accountName}` : accountId;
  };

  // Actions
  setTab = (tab: number) => {
    this.currentTab = tab;
  };

  setSelectedPeriod = (periodId: string) => {
    this.selectedPeriodId = periodId;
  };

  setOverrideDialogOpen = (open: boolean, override?: GLOverride) => {
    this.overrideDialogOpen = open;
    this.editingOverride = override || null;
  };

  setExportDialogOpen = (open: boolean) => {
    this.exportDialogOpen = open;
  };

  // Data loading
  loadData = async (companyId?: string) => {
    this.isLoading = true;
    this.error = null;
    const cid = companyId || this.companyId;

    try {
      await Promise.all([
        this.glAccountsStore.find({ query: { isActive: true, $limit: 1000 } }),
        this.departmentsStore.find({ query: { $limit: 200 } }),
        this.fiscalPeriodsStore.find({ query: { companyId: cid, $limit: 50, $sort: { year: -1, month: -1 } } }),
        this.evereeWorkerDataStore.find({ query: { companyId: cid, page: 0, size: 200 } }),
        this.payrollGlMappingsStore.find({ query: { companyId: cid, $limit: 200 } }),
        this.payrollGlOverridesStore.find({ query: { companyId: cid, $limit: 100 } }),
      ]);

      runInAction(() => {
        this.glAccounts = (this.glAccountsStore.entities || []) as unknown as glAccount[];
        this.departments = (this.departmentsStore.entities || []) as unknown as department[];
        this.fiscalPeriods = (this.fiscalPeriodsStore.entities || []) as unknown as fiscalPeriod[];

        const rawWorkers = (this.evereeWorkerDataStore.entities || []) as unknown as Record<string, unknown>[];
        this.workers = rawWorkers.map((w) => ({
          ...w,
          workerId: (w.workerId || w.id) as string,
        })) as unknown as evereeWorkerData[];

        // Load saved mapping rules
        const savedMappings = (this.payrollGlMappingsStore.entities || []) as unknown as Record<string, unknown>[];
        this.mappingRules = savedMappings.map((m) => ({
          glMappingId: m.glMappingId as string,
          departmentId: (m.departmentId as string) || null,
          earningType: m.earningType as EarningType,
          glAccountId: m.glAccountId as string,
          cashGlAccountId: (m.cashGlAccountId as string) || null,
        }));

        // Load saved overrides
        const savedOverrides = (this.payrollGlOverridesStore.entities || []) as unknown as Record<string, unknown>[];
        this.overrides = savedOverrides.map((o) => {
          const worker = this.workers.find((w) => w.workerId === o.workerId);
          return {
            overrideId: o.overrideId as string,
            workerId: o.workerId as string,
            workerName: worker ? `${worker.firstName} ${worker.lastName}` : (o.workerId as string),
            earningType: o.earningType as EarningType,
            glAccountId: o.glAccountId as string,
            cashGlAccountId: (o.cashGlAccountId as string) || null,
          };
        });

        // Auto-select first open period
        if (!this.selectedPeriodId && this.openPeriods.length > 0) {
          this.selectedPeriodId = this.openPeriods[0].id;
        }
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to load data";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  loadPayables = async () => {
    const period = this.selectedPeriod;
    if (!period) return;

    try {
      await this.evereePayablesStore.find({
        query: {
          companyId: this.companyId,
          paymentStatus: "PAID",
          $limit: 1000,
        },
      });

      runInAction(() => {
        this.payables = (this.evereePayablesStore.entities || []) as unknown as evereePayable[];
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to load payables";
      });
    }
  };

  // Mapping CRUD
  addMappingRule = (rule: GLMappingRule) => {
    this.mappingRules.push(rule);
  };

  updateMappingRule = (index: number, rule: GLMappingRule) => {
    if (index >= 0 && index < this.mappingRules.length) {
      this.mappingRules[index] = rule;
    }
  };

  removeMappingRule = (index: number) => {
    this.mappingRules.splice(index, 1);
  };

  addOverride = (override: GLOverride) => {
    this.overrides.push(override);
  };

  updateOverride = (index: number, override: GLOverride) => {
    if (index >= 0 && index < this.overrides.length) {
      this.overrides[index] = override;
    }
  };

  removeOverride = (index: number) => {
    this.overrides.splice(index, 1);
  };

  // Save mappings to backend
  saveMappings = async () => {
    this.isLoading = true;
    this.error = null;
    try {
      // Save mapping rules
      for (const rule of this.mappingRules) {
        if (rule.glMappingId) {
          await this.payrollGlMappingsStore.patch(rule.glMappingId, {
            earningType: rule.earningType,
            glAccountId: rule.glAccountId,
            cashGlAccountId: rule.cashGlAccountId,
          });
        } else {
          const result = await this.payrollGlMappingsStore.create({
            companyId: this.companyId,
            departmentId: rule.departmentId,
            earningType: rule.earningType,
            glAccountId: rule.glAccountId,
            cashGlAccountId: rule.cashGlAccountId,
          });
          runInAction(() => {
            rule.glMappingId = (result as Record<string, unknown>).glMappingId as string;
          });
        }
      }

      // Save overrides
      for (const override of this.overrides) {
        if (override.overrideId) {
          await this.payrollGlOverridesStore.patch(override.overrideId, {
            glAccountId: override.glAccountId,
            cashGlAccountId: override.cashGlAccountId,
          });
        } else {
          const result = await this.payrollGlOverridesStore.create({
            companyId: this.companyId,
            workerId: override.workerId,
            earningType: override.earningType,
            glAccountId: override.glAccountId,
            cashGlAccountId: override.cashGlAccountId,
          });
          runInAction(() => {
            override.overrideId = (result as Record<string, unknown>).overrideId as string;
          });
        }
      }

      runInAction(() => {
        this.successMessage = "Mappings saved successfully";
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to save mappings";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Journal entry generation
  generateJournalEntry = async () => {
    this.isGenerating = true;
    this.error = null;
    this.journalLines = [];

    try {
      await this.loadPayables();

      runInAction(() => {
        const lines: JournalLine[] = [];
        // Aggregate by GL account
        const debitAccumulator = new Map<string, number>();
        const creditAccumulator = new Map<string, number>();

        for (const payable of this.payables) {
          const amount = parseFloat(payable.earningAmount.amount) || 0;
          if (amount === 0) continue;

          const earningType = EVEREE_EARNING_MAP[payable.earningType] || "REG";

          // Check for worker-level override first
          const override = this.overrides.find(
            (o) => o.workerId === payable.externalWorkerId && o.earningType === earningType
          );

          let debitAccountId: string;
          let creditAccountId: string | null;

          if (override) {
            debitAccountId = override.glAccountId;
            creditAccountId = override.cashGlAccountId;
          } else {
            // Find department mapping
            // For now, use company-wide default (departmentId=null)
            const mapping = this.mappingRules.find(
              (r) => r.earningType === earningType && r.departmentId === null
            ) || this.mappingRules.find(
              (r) => r.earningType === earningType
            );

            if (!mapping) continue;
            debitAccountId = mapping.glAccountId;
            creditAccountId = mapping.cashGlAccountId;
          }

          // Accumulate debits
          debitAccumulator.set(
            debitAccountId,
            (debitAccumulator.get(debitAccountId) || 0) + amount
          );

          // Accumulate credits (cash account)
          if (creditAccountId) {
            creditAccumulator.set(
              creditAccountId,
              (creditAccumulator.get(creditAccountId) || 0) + amount
            );
          }
        }

        // Build debit lines
        debitAccumulator.forEach((amount, accountId) => {
          const acct = this.glAccounts.find((a) => a.id === accountId);
          lines.push({
            accountId,
            accountNumber: acct?.accountNumber || "",
            accountName: acct?.accountName || "",
            description: `Payroll - ${this.companyId}`,
            debitAmount: Math.round(amount * 100) / 100,
            creditAmount: 0,
          });
        });

        // Build credit lines
        creditAccumulator.forEach((amount, accountId) => {
          const acct = this.glAccounts.find((a) => a.id === accountId);
          lines.push({
            accountId,
            accountNumber: acct?.accountNumber || "",
            accountName: acct?.accountName || "",
            description: `Payroll Cash - ${this.companyId}`,
            debitAmount: 0,
            creditAmount: Math.round(amount * 100) / 100,
          });
        });

        const period = this.selectedPeriod;
        this.journalDescription = `Payroll Journal Entry - ${this.companyId} - ${period?.year || ""}/${String(period?.month || "").padStart(2, "0")}`;
        this.journalLines = lines;
        this.currentTab = 1; // Switch to preview tab
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to generate journal entry";
      });
    } finally {
      runInAction(() => {
        this.isGenerating = false;
      });
    }
  };

  // Post journal entry to backend
  postJournalEntry = async () => {
    if (!this.isBalanced || this.journalLines.length === 0) {
      this.error = "Journal entry must be balanced and have line items";
      return;
    }

    this.isPosting = true;
    this.error = null;

    try {
      // Create journal entry header
      const entry = await this.journalEntriesStore.create({
        entryDate: new Date().toISOString().split("T")[0],
        description: this.journalDescription,
        reference: `PAYROLL-${this.companyId}-${this.selectedPeriod?.year || ""}-${this.selectedPeriod?.month || ""}`,
        periodId: this.selectedPeriodId,
        companyId: this.companyId,
      });

      const entryId = (entry as Record<string, unknown>).id as string;

      // Create line items
      for (let i = 0; i < this.journalLines.length; i++) {
        const line = this.journalLines[i];
        await this.journalLineItemsStore.create({
          journalEntryId: entryId,
          lineNumber: i + 1,
          accountId: line.accountId,
          accountNumber: line.accountNumber,
          accountName: line.accountName,
          description: line.description,
          debitAmount: line.debitAmount,
          creditAmount: line.creditAmount,
        });
      }

      runInAction(() => {
        this.successMessage = `Journal entry posted successfully (${this.journalLines.length} line items)`;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to post journal entry";
      });
    } finally {
      runInAction(() => {
        this.isPosting = false;
      });
    }
  };

  // Reset
  reset = () => {
    this.currentTab = 0;
    this.selectedPeriodId = "";
    this.isLoading = false;
    this.isGenerating = false;
    this.isPosting = false;
    this.error = null;
    this.successMessage = null;
    this.journalLines = [];
    this.journalDescription = "";
    this.overrideDialogOpen = false;
    this.editingOverride = null;
    this.exportDialogOpen = false;
  };
}

export default PayrollGLMappingStore;
